import React, { useContext, useEffect, useState } from 'react'
import {
    TextField,
    Select,
    MenuItem,
    FormControl,
    Chip,
    Stack,
    SelectChangeEvent,
    ListSubheader,
    Card,
    CardContent,
    CardHeader,
    Typography,
} from '@mui/material'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes'
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons/faExclamationCircle'
import { KeDoContextType, QueryBuilderFieldProps } from './types'
import { KeDoContext } from '../../../kedo-context'
import { CompactButton } from '@/shared/ui/buttons'
import { listSubheaderProps } from '@/shared/ui/styles'

const QueryBuilderField: React.FC<QueryBuilderFieldProps> = ({
    field,
    index,
    dataField: initialDataField,
    onChange: onFieldChange,
    onDelete,
}) => {
    const kedo = useContext<KeDoContextType>(KeDoContext)

    const [id, setId] = useState<any>(field.id)
    const [uuid, setUuid] = useState<string>(field.uuid)
    const [type, setType] = useState<string | ''>(field.type ?? '')
    const [dataField, setDataField] = useState<any>(initialDataField)
    const [value, setValue] = useState<any>(field.value ?? '')
    const [error, setError] = useState<number | null>(null)

    const onChangeMultiple = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const values = Array.from(
            e.target.selectedOptions,
            (option) => option.value
        )

        const field: any = {
            id: id,
            value: values,
            type: type,
        }

        field[e.target.name] = values
        setValue(values)
        onFieldChange(index, field)
    }

    const onChange = (
        e: React.ChangeEvent<any> | SelectChangeEvent<string>
    ) => {
        const field: any = {
            id: id,
            value: value,
            type: type,
        }

        field[e.target.name] = e.target.value

        if (e.target.name === 'type') setType(e.target.value)
        if (e.target.name === 'value') setValue(e.target.value)
        onFieldChange(index, field)
    }

    const fetchField = () => {
        if (
            field.id === 'created_at' ||
            field.id === 'modified_at' ||
            field.id === 'created_by' ||
            field.id === 'modified_by'
        ) {
            setDataField(field)

            return
        }

        if (isNaN(field.id)) {
            return
        }

        const url = kedo.api().getDefDossierDefFieldEndpoint()
        kedo.api()
            .getCached(url + '/' + field.id)
            .then((response: any) => setDataField(response.data))
            .catch((err: any) => {
                setError(err.response.status)
            })
    }

    useEffect(() => {
        fetchField()
    }, [])

    const getFieldTitle = (dataField: any) => {
        if (dataField && dataField.name) {
            return dataField.name
        }

        if (!dataField || !dataField.id) {
            return ''
        }

        if (dataField.id === 'created_at') {
            return kedo.t('Created at')
        }
        if (dataField.id === 'created_by') {
            return kedo.t('Created by')
        }
        if (dataField.id === 'modified_at') {
            return kedo.t('Modified at')
        }
        if (dataField.id === 'modified_by') {
            return kedo.t('Modified by')
        }

        return dataField.id
    }

    const renderSettings = () => {
        if (!dataField) {
            return ''
        }

        let defField = dataField.def_field

        if (dataField.id === 'created_at' || dataField.id === 'modified_at') {
            defField = { type: 'systemdate' }
        } else if (
            dataField.id === 'created_by' ||
            dataField.id === 'modified_by'
        ) {
            defField = { type: 'systemuser' }
        }

        if (!defField || !defField.type) {
            return ''
        }

        const fieldType = defField.type

        switch (fieldType) {
            case 'list':
                return (
                    <Stack spacing={2}>
                        <FormControl fullWidth size="small">
                            <Typography
                                variant="body2"
                                component="label"
                                gutterBottom
                                sx={{ fontWeight: 'bold' }}
                            >
                                {kedo.t('Type')}
                            </Typography>
                            <Select
                                name="type"
                                value={type}
                                size="small"
                                displayEmpty
                                onChange={onChange}
                            >
                                <MenuItem value="">
                                    {kedo.t('Choose an option')}
                                </MenuItem>
                                <MenuItem value="equals">
                                    {kedo.t('is equal to')}
                                </MenuItem>
                                <MenuItem value="not_equals">
                                    {kedo.t('is NOT equal to')}
                                </MenuItem>
                                <MenuItem value="in">
                                    {kedo.t('is one of the selected values.')}
                                </MenuItem>
                                <MenuItem value="not_in">
                                    {kedo.t(
                                        'is NOT one of the selected values.'
                                    )}
                                </MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                            <Typography
                                variant="body2"
                                component="label"
                                gutterBottom
                                sx={{ fontWeight: 'bold' }}
                            >
                                {kedo.t('Value')}
                            </Typography>
                            <Select
                                name="value"
                                value={value}
                                size="small"
                                onChange={onChange}
                                displayEmpty
                            >
                                <MenuItem value="">
                                    {kedo.t('Choose an option')}
                                </MenuItem>
                                <MenuItem value="NULL">
                                    {kedo.t('Empty')}
                                </MenuItem>
                                {defField.def_field_lists?.map((opt: any) => (
                                    <MenuItem key={opt.id} value={opt.id}>
                                        {kedo
                                            .env()
                                            .translateItem(
                                                opt,
                                                'def_field_list'
                                            )}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                )

            case 'user':
            case 'systemuser':
                return (
                    <Stack spacing={2}>
                        <FormControl fullWidth size="small">
                            <Typography
                                variant="body2"
                                component="label"
                                gutterBottom
                                sx={{ fontWeight: 'bold' }}
                            >
                                {kedo.t('Type')}
                            </Typography>
                            <Select
                                name="type"
                                value={type}
                                size="small"
                                onChange={onChange}
                                displayEmpty
                            >
                                <MenuItem value="">
                                    {kedo.t('Choose an option')}
                                </MenuItem>
                                <MenuItem value="equals">
                                    {kedo.t('is equal to')}
                                </MenuItem>
                                <MenuItem value="not_equals">
                                    {kedo.t('is NOT equal to')}
                                </MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                            <Typography
                                variant="body2"
                                component="label"
                                gutterBottom
                                sx={{ fontWeight: 'bold' }}
                            >
                                {kedo.t('Value')}
                            </Typography>
                            <Select
                                name="value"
                                value={value}
                                size="small"
                                onChange={onChange}
                                displayEmpty
                            >
                                <MenuItem value="">
                                    {kedo.t('Choose an option')}
                                </MenuItem>
                                <MenuItem value="{user}">
                                    {kedo.t('the logged-in user himself')}
                                </MenuItem>
                                <MenuItem value="null">
                                    {kedo.t('Empty selection (0 users)')}
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                )

            case 'date':
            case 'systemdate':
                return (
                    <Stack spacing={2}>
                        <FormControl fullWidth size="small">
                            <Typography
                                variant="body2"
                                component="label"
                                gutterBottom
                                sx={{ fontWeight: 'bold' }}
                            >
                                {kedo.t('Type')}
                            </Typography>
                            <Select
                                name="type"
                                value={type}
                                size="small"
                                onChange={
                                    onChange as (
                                        e: SelectChangeEvent<string>
                                    ) => void
                                } // cast to avoid TS issues
                                displayEmpty
                                MenuProps={listSubheaderProps}
                            >
                                <MenuItem value="">
                                    {kedo.t('Choose an option')}
                                </MenuItem>

                                <ListSubheader>
                                    {kedo.t('Conditions')}
                                </ListSubheader>
                                <MenuItem value="equals">
                                    {kedo.t('Date equals to (exact match)')}
                                </MenuItem>
                                <MenuItem value="not_equals">
                                    {kedo.t(
                                        'Date is not equal to (exact match)'
                                    )}
                                </MenuItem>
                                <MenuItem value="date_gt">
                                    Date is greater &gt;
                                </MenuItem>
                                <MenuItem value="date_gte">
                                    Date is greater or equals to &gt;=
                                </MenuItem>
                                <MenuItem value="date_lt">
                                    Date is lesser &lt;
                                </MenuItem>
                                <MenuItem value="date_lte">
                                    Date is lesser or equals to &lt;=
                                </MenuItem>

                                <ListSubheader>{kedo.t('Add')}</ListSubheader>
                                <MenuItem value="max_days_old">
                                    Add number of days to content. The result
                                    must be in the future (or equals today).
                                </MenuItem>
                                <MenuItem value="min_days_old">
                                    Add number of days from content. The result
                                    must be in the past (or equals today).
                                </MenuItem>
                                <MenuItem value="max_weeks_old">
                                    Add number of weeks to content. The result
                                    must be in the future (or equals today).
                                </MenuItem>
                                <MenuItem value="min_weeks_old">
                                    Add number of weeks from content. The result
                                    must be in the past (or equals today).
                                </MenuItem>
                                <MenuItem value="max_months_old">
                                    Add number of months to content. The result
                                    must be in the future (or equals today).
                                </MenuItem>
                                <MenuItem value="min_months_old">
                                    Add number of months from content. The
                                    result must be in the past (or equals
                                    today).
                                </MenuItem>
                                <MenuItem value="max_days_to_go">
                                    Add number of days to content. The result
                                    must be in the future.
                                </MenuItem>
                                <MenuItem value="min_days_to_go">
                                    Add number of days from content. The result
                                    must be in the past.
                                </MenuItem>
                                <MenuItem value="max_years_old">
                                    Add number of years to content. The result
                                    must be in the future (or equals today).
                                </MenuItem>
                                <MenuItem value="min_years_old">
                                    Add number of years from content. The result
                                    must be in the past (or equals today).
                                </MenuItem>

                                <ListSubheader>
                                    {kedo.t('Subtract')}
                                </ListSubheader>
                                <MenuItem value="max_days_old_subtract">
                                    Subtract number of days to content. The
                                    result must be in the future (or equals
                                    today).
                                </MenuItem>
                                <MenuItem value="min_days_old_subtract">
                                    Subtract number of days from content. The
                                    result must be in the past (or equals
                                    today).
                                </MenuItem>
                                <MenuItem value="max_weeks_old_subtract">
                                    Subtract number of weeks to content. The
                                    result must be in the future (or equals
                                    today).
                                </MenuItem>
                                <MenuItem value="min_weeks_old_subtract">
                                    Subtract number of weeks from content. The
                                    result must be in the past (or equals
                                    today).
                                </MenuItem>
                                <MenuItem value="max_months_old_subtract">
                                    Subtract number of months to content. The
                                    result must be in the future (or equals
                                    today).
                                </MenuItem>
                                <MenuItem value="min_months_old_subtract">
                                    Subtract number of months from content. The
                                    result must be in the past (or equals
                                    today).
                                </MenuItem>
                                <MenuItem value="max_days_to_go_subtract">
                                    Subtract number of days to content. The
                                    result must be in the future.
                                </MenuItem>
                                <MenuItem value="min_days_to_go_subtract">
                                    Subtract number of days from content. The
                                    result must be in the past.
                                </MenuItem>
                                <MenuItem value="max_years_old_subtract">
                                    Subtract number of years to content. The
                                    result must be in the future (or equals
                                    today).
                                </MenuItem>
                                <MenuItem value="min_years_old_subtract">
                                    Subtract number of years from content. The
                                    result must be in the past (or equals today)
                                </MenuItem>

                                <ListSubheader
                                    sx={(theme) => ({
                                        fontSize: 16,
                                        fontWeight: 600,
                                        color: theme.palette.text.secondary,
                                        lineHeight: 1.5,
                                        py: 0.5,
                                        px: 1,
                                        backgroundColor:
                                            theme.palette.action.hover,
                                    })}
                                >
                                    {kedo.t('Between')}
                                </ListSubheader>
                                <MenuItem value="date_between">
                                    {
                                        'Date is between. Syntax for input field: DATE1,DATE2. The comma is mandatory.'
                                    }
                                </MenuItem>
                                <MenuItem value="between_days">
                                    {'Between given number of days and TODAY'}
                                </MenuItem>
                                <MenuItem value="between_weeks">
                                    {'Between given number of weeks and TODAY'}
                                </MenuItem>
                                <MenuItem value="between_months">
                                    {'Between given number of months and TODAY'}
                                </MenuItem>
                                <MenuItem value="between_years">
                                    {'Between given number of years and TODAY'}
                                </MenuItem>
                                <MenuItem value="birthday_weeks">
                                    {
                                        'Given content date on today OR between TODAY and given number of weeks'
                                    }
                                </MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <Typography
                                variant="body2"
                                component="label"
                                gutterBottom
                                sx={{ fontWeight: 'bold' }}
                            >
                                Value (enter a number, NULL, NOT NULL, or a
                                date):
                            </Typography>
                            <TextField
                                size="small"
                                name="value"
                                value={value}
                                onChange={(e) =>
                                    onChange({
                                        target: {
                                            name: 'value',
                                            value: e.target.value,
                                        },
                                    } as any)
                                }
                            />
                        </FormControl>
                    </Stack>
                )

            case 'textarea':
                return (
                    <Stack>
                        <FormControl fullWidth>
                            <Typography
                                variant="body2"
                                component="label"
                                gutterBottom
                                sx={{ fontWeight: 'bold' }}
                            >
                                {kedo.t('Type')}
                            </Typography>
                            <Select
                                name="type"
                                value={type}
                                size="small"
                                onChange={
                                    onChange as (
                                        e: SelectChangeEvent<string>
                                    ) => void
                                }
                                displayEmpty
                            >
                                <MenuItem value="">Choose an option</MenuItem>
                                <MenuItem value="string_empty">
                                    is EMPTY
                                </MenuItem>
                                <MenuItem value="string_is_not_empty">
                                    is NOT EMPTY
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                )

            case 'text':
            case 'autoIncrement':
            case 'calc':
            case 'url':
            case 'email':
            case 'amount':
                return (
                    <Stack spacing={2}>
                        <FormControl fullWidth>
                            <Typography
                                variant="body2"
                                component="label"
                                gutterBottom
                                sx={{ fontWeight: 'bold' }}
                            >
                                {kedo.t('Type')}
                            </Typography>
                            <Select
                                name="type"
                                value={type}
                                size="small"
                                onChange={
                                    onChange as (
                                        e: SelectChangeEvent<string>
                                    ) => void
                                }
                                displayEmpty
                            >
                                <MenuItem value="">Choose an option</MenuItem>
                                <MenuItem value="string_empty">
                                    is EMPTY
                                </MenuItem>
                                <MenuItem value="string_is_not_empty">
                                    is NOT EMPTY
                                </MenuItem>
                                <MenuItem value="string_exact_equals">
                                    is equal to (exact match)
                                </MenuItem>
                                <MenuItem value="string_exact_not_equals">
                                    is NOT equal to (exact match)
                                </MenuItem>
                                <MenuItem value="string_like_equals">
                                    IS LIKE (pattern)
                                </MenuItem>
                                <MenuItem value="string_not_like_equals">
                                    IS NOT LIKE (pattern)
                                </MenuItem>
                                <MenuItem value="string_numerical_equal">
                                    Numeric input: equal =
                                </MenuItem>
                                <MenuItem value="string_numerical_not_equal">
                                    Numeric input: not equal &lt;&gt;
                                </MenuItem>
                                <MenuItem value="string_numerical_bt">
                                    Numeric: &gt;
                                </MenuItem>
                                <MenuItem value="string_numerical_bte">
                                    Numeric: &gt;=
                                </MenuItem>
                                <MenuItem value="string_numerical_st">
                                    Numeric: &lt;
                                </MenuItem>
                                <MenuItem value="string_numerical_ste">
                                    Numeric: &lt;=
                                </MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <Typography
                                variant="body2"
                                component="label"
                                gutterBottom
                                sx={{ fontWeight: 'bold' }}
                            >
                                {kedo.t('Value')}
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                name="value"
                                value={value}
                                onChange={(e) =>
                                    onChange({
                                        target: {
                                            name: 'value',
                                            value: e.target.value,
                                        },
                                    } as any)
                                }
                                InputProps={{
                                    readOnly:
                                        type === 'string_empty' ||
                                        type === 'string_is_not_empty',
                                }}
                            />
                        </FormControl>
                    </Stack>
                )

            default:
                return null
        }
    }

    return (
        <Card sx={{ mb: 3 }}>
            <CardHeader
                title={getFieldTitle(dataField)}
                sx={{
                    bgcolor: 'grey.900',
                    color: 'common.white',
                    py: 1.5,
                    px: 2.5,
                }}
                slotProps={{
                    action: {
                        sx: {
                            m: 0,
                            alignSelf: 'center',
                        },
                    },
                }}
                action={
                    <CompactButton dense onClick={() => onDelete(uuid)}>
                        <FontAwesomeIcon icon={faTimes} />
                    </CompactButton>
                }
                subheader={
                    <Stack direction="row" spacing={1} mt={1}>
                        {error === 404 && (
                            <Chip
                                color="warning"
                                size="small"
                                icon={
                                    <FontAwesomeIcon
                                        icon={faExclamationCircle}
                                    />
                                }
                                label={kedo.t('Item not found')}
                            />
                        )}
                        {error && error !== 404 && (
                            <Chip
                                color="warning"
                                size="small"
                                icon={
                                    <FontAwesomeIcon
                                        icon={faExclamationCircle}
                                    />
                                }
                                label={kedo.t('Something went wrong')}
                            />
                        )}
                    </Stack>
                }
            />

            <CardContent sx={{ bgcolor: 'grey.100' }}>
                {renderSettings()}
            </CardContent>
        </Card>
    )
}

export default QueryBuilderField
